// P4 — the specialism consumer core. Reconciles cited web-evidence specialism
// signals into the clinical-specialism axis with an honest provenance tier:
//   • web_confirmed — the entity's OWN identity-verified (HIGH) site states it (cited)
//   • web_suggested — stated on a lower-identity site, OR inherited from a group sibling
// Multi-valued (one site → many specialisms). Group cascade is GATED by the member's
// sub-segment (a specialism only cascades to a member whose canonical sub-segment is a
// parent of that specialism) — so a group's care-home specialism never lands on its
// pharmacy member. NEVER collapses the calibration-locked primary_specialty; this is a
// parallel evidence overlay. Pure + deterministic; the DB read/write is a thin wrapper.

import { CLINICAL_SPECIALISMS } from './blueprints/specialisms.js';

export type SpecialismStatusValue = 'web_confirmed' | 'web_suggested';

export interface WebSpecialismInput {
  token: string;
  signalId?: string | null;
  confidence?: number | null;
}

export interface EntitySpecialismInput {
  entityId: string;
  identityTier: string | null; // 'high' | 'medium' | 'low' | 'none'
  subsegment: string | null; // canonical sub-segment (for the cascade gate)
  tokens: WebSpecialismInput[];
}

export interface SpecialismStatus {
  entityId: string;
  specialism: string;
  status: SpecialismStatusValue;
  source: 'direct' | 'group_cascade';
  sourceSignalId: string | null;
  groupKey: string | null;
  identityTier: string | null;
  confidence: number | null;
}

// specialism token → its parent canonical sub-segments (the cascade gate).
const SPECIALISM_PARENTS = new Map<string, Set<string>>(
  CLINICAL_SPECIALISMS.map((s) => [s.token, new Set(s.parents as string[])]),
);

/** Reconcile web specialism signals → per-(entity, specialism) status rows. */
export function reconcileSpecialisms(
  entities: EntitySpecialismInput[],
  groups: Map<string, string[]> = new Map(), // groupKey → member entityIds
): SpecialismStatus[] {
  const byId = new Map(entities.map((e) => [e.entityId, e]));
  const out: SpecialismStatus[] = [];
  const has = new Map<string, Set<string>>(); // entityId → tokens already assigned

  // 1. Direct — the entity's own site. HIGH identity ⇒ confirmed, else suggested.
  for (const e of entities) {
    const seen = has.get(e.entityId) ?? new Set<string>();
    for (const t of e.tokens) {
      if (seen.has(t.token)) continue;
      seen.add(t.token);
      out.push({
        entityId: e.entityId,
        specialism: t.token,
        status: e.identityTier === 'high' ? 'web_confirmed' : 'web_suggested',
        source: 'direct',
        sourceSignalId: t.signalId ?? null,
        groupKey: null,
        identityTier: e.identityTier,
        confidence: t.confidence ?? null,
      });
    }
    has.set(e.entityId, seen);
  }

  // 2. Group cascade — a group's confirmed specialisms flow to members, GATED by the
  //    member's sub-segment. Cascaded tags are 'web_suggested' (inferred, not cited).
  for (const [groupKey, members] of groups) {
    const groupTokens = new Set<string>();
    for (const m of members) {
      const e = byId.get(m);
      if (e && e.identityTier === 'high') for (const t of e.tokens) groupTokens.add(t.token);
    }
    if (groupTokens.size === 0) continue;
    for (const m of members) {
      const e = byId.get(m);
      const sub = e?.subsegment ?? null;
      if (!sub) continue; // no sub-segment ⇒ cannot gate ⇒ no cascade
      const seen = has.get(m) ?? new Set<string>();
      for (const tok of groupTokens) {
        if (seen.has(tok)) continue; // already has it (direct or cascaded)
        const parents = SPECIALISM_PARENTS.get(tok);
        if (!parents || !parents.has(sub)) continue; // sub-segment gate
        seen.add(tok);
        out.push({
          entityId: m,
          specialism: tok,
          status: 'web_suggested',
          source: 'group_cascade',
          sourceSignalId: null,
          groupKey,
          identityTier: e?.identityTier ?? null,
          confidence: null,
        });
      }
      has.set(m, seen);
    }
  }
  return out;
}
